import React, { useCallback, useEffect, useState } from "react";
import { CreditCard, Check, X, Loader2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

const chf = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const WALLET_LABEL = { apple_pay: "Apple Pay", google_pay: "Google Pay", samsung_pay: "Samsung Pay", wallet: "Wallet" };

/** Posteingang: Karte taucht auf ≥3 offenen Belegen auf, ist aber nicht hinterlegt → Vorschlag «Als Firmenkarte übernehmen». */
export function CardSuggestionsBanner({ companyId, onApplied }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [labelFor, setLabelFor] = useState(null);
  const [label, setLabel] = useState("");
  const [privateFor, setPrivateFor] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");

  const load = useCallback(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/cards/suggestions`)
      .then((r) => setItems((r.data?.items || []).filter((s) => (s.receipt_count || 0) >= 3 || s.suggested_kind === "wallet_alias")))
      .catch(() => setItems([]));
  }, [companyId]);
  useEffect(load, [load]);

  const register = async (s, kind) => {
    setBusy(s.last4);
    try {
      const body = kind === "wallet_alias"
        ? { last4: s.last4, brand: s.brand || null, kind: "company", alias_of_last4: s.alias_of_last4, wallet: s.wallet }
        : kind === "company"
        ? { last4: s.last4, brand: s.brand || null, kind: "company", label: label || `Firmenkarte …${s.last4}` }
        : { last4: s.last4, brand: s.brand || null, kind: "employee_private", holder_employee_id: employeeId };
      const r = await apiClient.post(`/companies/${companyId}/cards`, body);
      const n = r.data?.applied_to_pending || 0;
      toast.success(`${kind === "wallet_alias" ? `${WALLET_LABEL[s.wallet] || "Wallet"}-Nummer` : kind === "company" ? "Firmenkarte" : "Privatkarte"} …${s.last4} hinterlegt${kind === "wallet_alias" ? ` (Karte …${s.alias_of_last4})` : ""} · auf ${n} offene Belege angewendet`);
      setLabelFor(null); setPrivateFor(null); setLabel(""); setEmployeeId("");
      load();
      onApplied?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Karte konnte nicht gespeichert werden"); }
    finally { setBusy(null); }
  };

  const openPrivate = async (s) => {
    setPrivateFor(s.last4); setLabelFor(null);
    try {
      const r = await apiClient.get(`/companies/${companyId}/employees`);
      setEmployees((r.data || []).filter((e) => !e.archived_at));
    } catch { setEmployees([]); }
  };

  const dismiss = async (s) => {
    try {
      await apiClient.post(`/companies/${companyId}/cards/suggestions/${s.last4}/dismiss`);
      setItems((prev) => prev.filter((x) => x.last4 !== s.last4));
    } catch { toast.error("Vorschlag konnte nicht ausgeblendet werden"); }
  };

  if (!items.length) return null;
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="card-suggestions-banner">
      <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
        <CreditCard className="h-4 w-4" /> Karte mehrfach erkannt – ist das eine Firmenkarte?
        <span className="text-xs font-normal text-amber-800">Einmal hinterlegen, dann verschwinden die Warnungen «Fremde Karte» auf allen Belegen.</span>
      </div>
      {items.map((s) => (
        <div key={s.last4} className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm flex flex-wrap items-center gap-3" data-testid={`card-suggestion-row-${s.last4}`}>
          <span className="font-mono font-medium">…{s.last4}</span>
          <span className="text-slate-700">{s.brand || "Karte"}</span>
          {s.suggested_kind === "wallet_alias" ? (
            <span className="text-xs text-slate-700" data-testid={`card-suggestion-reason-${s.last4}`}><strong>{WALLET_LABEL[s.wallet] || "Wallet"} von Karte …{s.alias_of_last4}?</strong> {s.reason}{s.examples?.[0] ? ` · z.B. ${s.examples[0]}` : ""}</span>
          ) : (
            <span className="text-xs text-slate-600" data-testid={`card-suggestion-reason-${s.last4}`}>{s.receipt_count} offene Belege{s.receipt_total ? ` · ${chf(s.receipt_total)}` : ""}{s.count ? ` · ${s.count}× im Bankauszug` : ""}</span>
          )}
          {s.merchants?.length > 0 && <span className="text-xs text-slate-500 truncate">z.B. {s.merchants.join(", ")}</span>}
          <div className="ml-auto flex flex-wrap items-center gap-1">
            {labelFor === s.last4 ? (
              <>
                <input className="h-8 w-44 rounded border border-slate-300 px-2 text-xs" autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Inhaber / Bezeichnung" data-testid={`card-suggestion-banner-label-${s.last4}`} />
                <Button size="sm" className="h-8" disabled={busy === s.last4} onClick={() => register(s, "company")} data-testid={`card-suggestion-banner-save-${s.last4}`}>
                  {busy === s.last4 ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Speichern"}
                </Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => setLabelFor(null)}>Abbrechen</Button>
              </>
            ) : privateFor === s.last4 ? (
              <>
                <select className="h-8 rounded border border-slate-300 bg-white px-2 text-xs" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} data-testid={`card-suggestion-banner-employee-${s.last4}`}>
                  <option value="">Mitarbeiter wählen …</option>
                  {employees.map((e) => <option key={e._id || e.id} value={e._id || e.id}>{e.first_name} {e.last_name}</option>)}
                </select>
                <Button size="sm" className="h-8" disabled={busy === s.last4 || !employeeId} onClick={() => register(s, "employee_private")} data-testid={`card-suggestion-banner-save-private-${s.last4}`}>Speichern</Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => setPrivateFor(null)}>Abbrechen</Button>
              </>
            ) : s.suggested_kind === "wallet_alias" ? (
              <>
                <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" disabled={busy === s.last4} onClick={() => register(s, "wallet_alias")} data-testid={`card-suggestion-banner-alias-${s.last4}`}>
                  <Check className="h-3.5 w-3.5 mr-1" /> Als {WALLET_LABEL[s.wallet] || "Wallet"}-Nummer von …{s.alias_of_last4} speichern
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => { setLabelFor(s.last4); setPrivateFor(null); setLabel(""); }} data-testid={`card-suggestion-banner-accept-${s.last4}`}>Eigene Firmenkarte</Button>
                <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => dismiss(s)} data-testid={`card-suggestion-banner-dismiss-${s.last4}`}>
                  <X className="h-3.5 w-3.5 mr-1" /> Ignorieren
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" className="h-8 bg-amber-600 hover:bg-amber-700" onClick={() => { setLabelFor(s.last4); setPrivateFor(null); setLabel(""); }} data-testid={`card-suggestion-banner-accept-${s.last4}`}>
                  <Check className="h-3.5 w-3.5 mr-1" /> Als Firmenkarte übernehmen
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => openPrivate(s)} data-testid={`card-suggestion-banner-private-${s.last4}`}>
                  <UserRound className="h-3.5 w-3.5 mr-1" /> Privatkarte (Spesen)
                </Button>
                <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => dismiss(s)} data-testid={`card-suggestion-banner-dismiss-${s.last4}`}>
                  <X className="h-3.5 w-3.5 mr-1" /> Ignorieren
                </Button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
